import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'

type Language = 'en' | 'ar'

interface LanguageContextType {
  language: Language
  setLanguage: (lang: Language) => void
  isRTL: boolean
  toggleLanguage: () => void
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined)

interface LanguageProviderProps {
  children: ReactNode
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguage] = useState<Language>('en')

  // Load saved language preference on mount
  useEffect(() => {
    const savedLanguage = localStorage.getItem('preferred-language') as Language
    if (savedLanguage && (savedLanguage === 'en' || savedLanguage === 'ar')) {
      setLanguage(savedLanguage)
    }
  }, [])

  // Update HTML attributes and save preference when language changes
  useEffect(() => {
    const html = document.documentElement
    
    // Update HTML lang attribute
    html.setAttribute('lang', language)
    
    // Update HTML dir attribute
    html.setAttribute('dir', language === 'ar' ? 'rtl' : 'ltr')
    
    // Add/remove RTL class for styling
    if (language === 'ar') {
      html.classList.add('rtl')
    } else {
      html.classList.remove('rtl')
    }
    
    // Save to localStorage
    localStorage.setItem('preferred-language', language)
  }, [language])

  const toggleLanguage = () => {
    setLanguage(current => current === 'en' ? 'ar' : 'en')
  }

  const isRTL = language === 'ar'

  return (
    <LanguageContext.Provider value={{ 
      language, 
      setLanguage, 
      isRTL, 
      toggleLanguage 
    }}>
      {children}
    </LanguageContext.Provider>
  )
}

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext)
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider')
  }
  return context
}

export default LanguageProvider